import React from 'react';
import { Heart, Award, Sparkles, Users } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations/translations';

const AboutUs = () => {
  const { language } = useLanguage();

  const values = [
    { icon: Award, title: t(language, 'about.value1Title'), desc: t(language, 'about.value1Desc') },
    { icon: Sparkles, title: t(language, 'about.value2Title'), desc: t(language, 'about.value2Desc') },
    { icon: Heart, title: t(language, 'about.value3Title'), desc: t(language, 'about.value3Desc') },
    { icon: Users, title: t(language, 'about.value4Title'), desc: t(language, 'about.value4Desc') },
  ];

  return (
    <section id="sobre-nosotros" className="py-10 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-slate-900 text-white text-[11px] font-semibold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full mb-4">
            <span className="w-1 h-1 rounded-full bg-cyan-400" />
            {t(language, 'about.badge')}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-3 tracking-tight">
            {t(language, 'about.title')}
          </h2>
          <p className="text-base md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
            {t(language, 'about.subtitle')}
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 mb-10 md:mb-16">
          {/* Image */}
          <div className="relative h-72 sm:h-96 rounded-2xl overflow-hidden ring-1 ring-slate-200">
            <img
              src="https://images.pexels.com/photos/5985967/pexels-photo-5985967.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Tzitzit tradicional"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-slate-900/5 to-transparent" />
          </div>

          {/* Description */}
          <div className="flex flex-col justify-center space-y-5">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              {t(language, 'about.description')}
            </p>

            <div className="relative bg-slate-900 text-white rounded-2xl p-5 md:p-6 overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
                  <Heart className="h-4 w-4 text-cyan-400" />
                </div>
                <p className="text-sm md:text-base font-medium leading-relaxed text-slate-100">
                  {t(language, 'about.mission')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Values Grid — unified monochrome style */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {values.map((v, index) => {
            const Icon = v.icon;
            return (
              <div
                key={index}
                data-testid={`about-value-${index}`}
                className="group relative rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-900/20 hover:shadow-lg transition-all duration-300 p-4 sm:p-6"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-slate-100 group-hover:bg-slate-900 flex items-center justify-center mb-3 transition-colors duration-300">
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-700 group-hover:text-cyan-400 transition-colors duration-300" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-1 leading-tight">
                  {v.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                  {v.desc}
                </p>
                {/* Subtle number indicator top-right */}
                <span className="absolute top-3 right-4 text-[10px] font-bold text-slate-300 tracking-widest">
                  0{index + 1}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
